/* ============================================================
   CartPage — cart lines, quantity editing, checkout
   ============================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Cart, OrderSummary, WebDeliveryMethod } from '../types/product';
import type { PublicPickupPoint } from '../types/product';
import { SAME_CITY_DELIVERY_RUB, deliveryMethodToJson, formatRub } from '../types/product';
import type { AddressBookEntry } from '../types/address';
import { AddressInput } from './AddressInput';
import { resolveMediaUrl } from '../api/client';
import { cartColorCaption, cartHexByColor, cartOffersModeChips, cartPrintColorChoice } from './cartColorView';
import { hexForColorName } from './model3dView';
import { Model3DPreview } from './Model3DPreview';
import { pickupBlock, pickupPerformer, carrierPointBlock, showPickupMap } from './cartPickupView';
import { CarrierPickupMapPanel } from './CarrierPickupMapPanel';
import { sameCity } from './carrierPickupView';
import type { CarrierPointSelection } from '../types/delivery';
import { resolveSelection } from './performerColorsView';
import { ANY_COLOR_CHIP, MULTICOLOR_CHIP, colorModeNote, fallbackHexFor, swatchDotStyle } from './colorModeView';
import {
  canOfferSaveToBook,
  entrySubtitle,
  entryTitle,
  initialChooser,
  submitAddress,
} from './addressBookView';
import {
  buildCheckoutPhoneDraft,
  checkoutNeedsPhone,
  checkoutPhoneGate,
  parseCheckoutPhone,
} from './checkoutPhoneView';
import { normalizeDeliveryMethod } from '../types/product';

interface Props {
  onNeedAuth: () => void;
}

/* G8-D1: light-theme method chip — selected state via the accent border
   (same shape as the PerformerList chips, DESIGN-SYSTEM.md). */
function DeliveryChip({ active, label, price, onClick }: {
  active: boolean;
  label: string;
  price: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--space-xs)',
        padding: 'var(--space-xs) var(--space-md)',
        borderRadius: 'var(--radius-md)',
        border: active
          ? '2px solid var(--color-primary)'
          : '1px solid var(--color-separator)',
        background: 'var(--color-surface)',
        cursor: 'pointer',
        color: active ? 'var(--color-primary)' : 'var(--color-text-primary)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        fontSize: 'var(--fs-body)',
      }}
    >
      <span>{label}</span>
      <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{price}</span>
    </button>
  );
}

export function CartPage({ onNeedAuth }: Props) {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState<Cart | null>(null);
  const [address, setAddress] = useState('');
  // CB-3: address book chooser — default entry preselected, free-form fallback.
  const [book, setBook] = useState<AddressBookEntry[]>([]);
  const [chooserMode, setChooserMode] = useState<'book' | 'custom'>('custom');
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [saveToBook, setSaveToBook] = useState(false);
  const [notes, setNotes] = useState('');
  // G8-D1: checkout delivery chips — applies to orders without quote-print
  // delivery lines (print jobs keep the method chosen per line in the cart).
  const [deliveryMethod, setDeliveryMethod] = useState<WebDeliveryMethod>('Courier');
  // MS-2 (TASK-004f86db): print color for quote-print orders — offered from
  // the standard palette while the cart has print lines (stock palettes are a
  // matching-time enrichment, MS-1; the cart rides the palette posture).
  const [printColor, setPrintColor] = useState<string | null>(null);
  // ecfe2257 (Artem 2026-08-28 §5): how the color is pinned — concrete pick
  // above, or the «Многоцветная»/«Любой цвет» chips (total-stock only).
  const [colorMode, setColorMode] = useState<'concrete' | 'multicolor' | 'any'>('concrete');
  // 3830fe0d: pickup-point pick at checkout for Pickup quote-print lines
  // (the per-line method was chosen on the quote page; the point itself is
  // chosen here — G8-D2 server applies CheckoutDto.PickupPointId).
  const [pickupPoints, setPickupPoints] = useState<PublicPickupPoint[] | null>(null);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [pickupPointId, setPickupPointId] = useState<string | null>(null);
  // 63b51a7e: carrier ПВЗ picked on the checkout map (Yandex Maps picker,
  // non-blocking — rides CheckoutDto only when chosen).
  const [carrierPoint, setCarrierPoint] = useState<CarrierPointSelection | null>(null);
  // 2026-09-03 (Артем): the city picked on the checkout map — powers the
  // courier rule (courier only same-city with a single performer).
  const [pickupCity, setPickupCity] = useState('');
  // 527b0a7f: recipient phone — prefilled from the profile, editable at
  // checkout; snapshotted onto every order server-side (a first-typed
  // number is saved back to the profile there).
  const [phone, setPhone] = useState('');
  const [phoneLoaded, setPhoneLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState<OrderSummary[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setCart(await api.getCart());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки корзины');
    }
  }, []);

  // CB-3: best-effort address book load — a failure degrades to the plain
  // free-form input (management lives in the profile drawer).
  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .getAddressBook()
      .then((entries) => {
        setBook(entries);
        const initial = initialChooser(entries);
        setChooserMode(initial.mode);
        setSelectedAddressId(initial.selectedId);
      })
      .catch(() => setBook([]));
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) void load();
  }, [isAuthenticated, load]);

  // 527b0a7f: best-effort profile prefill — a failure leaves the field empty
  // (the number can always be typed at checkout). Loaded once; the customer's
  // edits are never overwritten afterwards.
  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .getProfile()
      .then((p) => {
        if (!phoneLoaded) setPhone(buildCheckoutPhoneDraft(p.phoneNumber));
      })
      .catch(() => undefined)
      .finally(() => setPhoneLoaded(true));
  }, [isAuthenticated, phoneLoaded]);

  // MS-2: resolve the picker posture for the current cart and keep the pick
  // valid when lines change (pure helpers — tested in node).
  const colorChoice = useMemo(() => cartPrintColorChoice(cart?.items), [cart]);
  // ecfe2257: hex lookup across lines' swatches + mode-chip availability.
  const hexByColor = useMemo(() => cartHexByColor(cart?.items), [cart]);
  const offerModeChips = useMemo(() => cartOffersModeChips(cart?.items), [cart]);
  useEffect(() => {
    setPrintColor((current) => resolveSelection(colorChoice, current));
  }, [colorChoice]);

  // 3830fe0d: the performer whose Pickup print line awaits a point.
  const pickupNeed = useMemo(() => pickupPerformer(cart?.items), [cart]);
  // 2026-09-03 (Артем): courier offer rule — exactly one performer and the
  // map city must match the performer's own city. The map loads the
  // performer's points for the same single-performer case.
  const singlePerformerId = useMemo(() => {
    const ids = new Set(
      (cart?.items ?? [])
        .map((i) => i.performerId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    );
    return ids.size === 1 ? [...ids][0] ?? null : null;
  }, [cart]);
  const providerCity = cart?.items.find((i) => i.providerCity)?.providerCity ?? null;
  const courierAvailable = singlePerformerId !== null && !!providerCity && sameCity(providerCity, pickupCity);
  const pickupLayerPerformer = singlePerformerId ?? pickupNeed?.performerId ?? null;
  const handlePickupSelect = useCallback((id: string | null) => {
    setPickupPointId(id);
    setDeliveryMethod(id ? 'Pickup' : 'DropoffPoint');
  }, []);
  useEffect(() => {
    if (!courierAvailable && deliveryMethod === 'Courier') setDeliveryMethod('DropoffPoint');
  }, [courierAvailable, deliveryMethod]);
  useEffect(() => {
    setPickupPointId(null);
    setPickupPoints(null);
    if (!pickupLayerPerformer) return;
    let cancelled = false;
    setLoadingPoints(true);
    api
      .getProviderPickupPoints(pickupLayerPerformer)
      .then((points) => {
        if (cancelled) return;
        setPickupPoints(points);
        if (pickupNeed && points.length > 0) setPickupPointId(points[0].pickupPointId);
      })
      .catch(() => {
        if (!cancelled) setPickupPoints([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPoints(false);
      });
    return () => { cancelled = true; };
  }, [pickupLayerPerformer]);

  if (!isAuthenticated) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)' }}>
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Корзина доступна после входа</p>
          <button className="btn btn-primary" onClick={onNeedAuth} style={{ marginTop: 'var(--space-md)' }}>
            Войти
          </button>
        </div>
      </div>
    );
  }

  const updateQty = async (itemId: string, quantity: number) => {
    setBusy(true);
    setError(null);
    try {
      setCart(await api.updateCartItem(itemId, quantity));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (itemId: string) => {
    setBusy(true);
    setError(null);
    try {
      setCart(await api.removeCartItem(itemId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const checkout = async () => {
    setBusy(true);
    setError(null);
    const shipping = submitAddress(chooserMode, selectedAddressId, book, address);
    try {
      // CB-3: free-form text may be saved to the book on the way out — a
      // failure here must never block the order (best-effort, then checkout).
      if (saveToBook && chooserMode === 'custom' && shipping) {
        try {
          await api.createAddressBookEntry({
            label: null,
            addressLine: shipping,
            isDefault: book.length === 0,
          });
        } catch {
          // Book save failed — the order still goes out with the typed address.
        }
      }
      // 527b0a7f: the typed phone rides the checkout (trimmed; null keeps
      // the payload identical — the server then falls back to the profile).
      const typedPhone = parseCheckoutPhone(phone);
      // b0bef46e: the carrier point rides the checkout only for non-courier
      // methods — courier-by-address is the plain address form (no ПВЗ).
      const ridingCarrierPoint = deliveryMethod !== 'Courier' ? carrierPoint : null;
      const orders = await api.checkoutCart(
        shipping,
        notes.trim() || null,
        deliveryMethodToJson(deliveryMethod),
        pickupPointId ?? undefined,
        colorMode === 'concrete' ? printColor ?? undefined : undefined,
        colorMode === 'multicolor' ? 1 : colorMode === 'any' ? 0 : 2,
        ridingCarrierPoint,
        typedPhone.ok ? typedPhone.phone : null,
      );
      setPlaced(orders);
      setCart({ items: [], subtotalRub: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось оформить заказ');
    } finally {
      setBusy(false);
    }
  };

  if (placed) {
    // SD-3 (d36195ae): while the Safe Deal rail is on, checkout attaches a
    // YooKassa redirect per order (SD-2 PaymentConfirmationUrl) — the
    // placed screen turns into the pay step: per-order CTA opens the
    // confirmation page (return_url brings the customer back). Rail off
    // ⇒ every URL null ⇒ exactly today's confirmation screen.
    const payable = placed.filter((o) => o.paymentConfirmationUrl);
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)', maxWidth: 720 }}>
        <div className="card" style={{ padding: 'var(--space-xl)', textAlign: 'center' }}>
          <div style={{ fontSize: 48 }}>{payable.length > 0 ? '🔒' : '🎉'}</div>
          <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)', marginTop: 'var(--space-sm)' }}>
            Заказ{placed.length > 1 ? 'ы' : ''} оформлен{placed.length > 1 ? 'ы' : ''}!
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            {payable.length > 0
              ? (payable.length === 1
                  ? 'Оплатите заказ через безопасный платёж — деньги удерживаются сервисом до завершения.'
                  : `Корзина разделена на ${placed.length} заказа — оплатите каждый через безопасный платёж.`)
              : (placed.length === 1
                  ? 'Исполнитель увидит заказ и примет его в работу.'
                  : `Корзина разделена на ${placed.length} заказа — по одному на каждого исполнителя.`)}
          </p>
          <div style={{ marginTop: 'var(--space-lg)', display: 'grid', gap: 'var(--space-sm)' }}>
            {placed.map((o) => (
              <div
                key={o.orderId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--space-sm)',
                  padding: 'var(--space-sm) var(--space-md)',
                  background: 'var(--color-bg)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-caption)' }}>
                  №{o.orderId.slice(0, 8).toUpperCase()}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <span style={{ fontWeight: 'var(--fw-semibold)' }}>{formatRub(o.totalPrice)}</span>
                  {o.paymentConfirmationUrl && (
                    <button
                      className="btn btn-primary"
                      style={{ height: 36, padding: '6px 14px' }}
                      onClick={() => window.location.assign(o.paymentConfirmationUrl!)}
                    >
                      Оплатить
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 527b0a7f: recipient-phone gate — required for courier/carrier-ПВЗ
  // deliveries (someone must be reachable), optional for pure self-pickup;
  // a typed-but-invalid value always blocks. Pure helper — tested in node.
  const phoneNeed = {
    deliveryMethod,
    hasCourierLines: (cart?.items.some(
      (i) => normalizeDeliveryMethod(i.deliveryMethod ?? null) === 'Courier',
    ) ?? false),
    carrierPointChosen: carrierPoint !== null,
  };
  const phoneRequired = checkoutNeedsPhone(phoneNeed);
  const phoneGate = checkoutPhoneGate(phone, phoneNeed);

  // b0bef46e: unified pickup-point contract — the ПВЗ chip requires a chosen
  // carrier point (the server rejects a point-less DropoffPoint as well).
  const mapBlock = carrierPointBlock(deliveryMethod, carrierPoint !== null);

  const hasBlocking = (cart?.items.some((i) => i.quantityError) ?? false)
    || pickupBlock(pickupNeed, pickupPointId, pickupPoints) !== null
    || phoneGate.blocks
    || mapBlock !== null;

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)', maxWidth: 860 }}>
      <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em', marginBottom: 'var(--space-lg)' }}>
        Корзина
      </h1>

      {error && (
        <div className="card" style={{ borderColor: 'var(--color-danger)', marginBottom: 'var(--space-md)', padding: 'var(--space-md)' }}>
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--fs-body)' }}>{error}</p>
        </div>
      )}

      {!cart ? (
        <div style={{ textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : cart.items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Корзина пуста</p>
          <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            Загляните в магазин — там готовые модели с фиксированной ценой
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          {cart.items.map((item) => (
            <div key={item.itemId} className="card" style={{ padding: 'var(--space-md)', display: 'flex', gap: 'var(--space-md)', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* cc4909b4: interactive 3D tile for quote-print lines with a
                  mesh (STL/OBJ/3MF + WebGL); the ME-2 PNG tile stays the
                  fallback, the monogram stays for lines without a model. */}
              {item.modelFileId ? (
                <Model3DPreview
                  fileId={item.modelFileId}
                  format={item.modelFileFormat}
                  previewUrl={item.previewUrl}
                  colorName={printColor}
                  colorHex={hexForColorName(printColor, item.stockSwatches)}
                  alt=""
                  height={96}
                  style={{ width: 96, flexShrink: 0 }}
                />
              ) : item.previewUrl ? (
                <img
                  src={resolveMediaUrl(item.previewUrl)}
                  alt=""
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-separator)',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 'var(--radius-md)',
                    background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 'var(--fs-h3)',
                    fontWeight: 'var(--fw-bold)',
                    flexShrink: 0,
                  }}
                >
                  {item.title.slice(0, 1).toUpperCase()}
                </div>
              )}

              <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                <div style={{ fontWeight: 'var(--fw-semibold)' }}>{item.title}</div>
                {/* a1a3b8f4: chosen product options on the line */}
                {item.optionsSummary && (
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{item.optionsSummary}</div>
                )}
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  {item.providerName} · {formatRub(item.unitPriceRub)} / шт.
                </div>
                {item.quantityError && (
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', marginTop: 'var(--space-xs)' }}>
                    ⚠ {item.quantityError}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                <button
                  className="btn btn-secondary"
                  style={{ width: 36, height: 36, padding: 0 }}
                  disabled={busy}
                  onClick={() => void updateQty(item.itemId, item.quantity - 1)}
                  aria-label="Меньше"
                >
                  −
                </button>
                <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 'var(--fw-semibold)' }}>{item.quantity}</span>
                <button
                  className="btn btn-secondary"
                  style={{ width: 36, height: 36, padding: 0 }}
                  disabled={busy}
                  onClick={() => void updateQty(item.itemId, item.quantity + 1)}
                  aria-label="Больше"
                >
                  +
                </button>
              </div>

              <div style={{ fontWeight: 'var(--fw-bold)', minWidth: 90, textAlign: 'right' }}>
                {formatRub(item.lineTotalRub)}
              </div>

              <button
                onClick={() => void remove(item.itemId)}
                disabled={busy}
                style={{ color: 'var(--color-danger)', fontSize: 'var(--fs-caption)' }}
              >
                Удалить
              </button>
            </div>
          ))}

          {/* Checkout */}
          <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Товары ({cart.items.reduce((s, i) => s + i.quantity, 0)})</span>
              <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)' }}>{formatRub(cart.subtotalRub)}</span>
            </div>
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Сервисный сбор добавляется при оформлении. Если в корзине товары разных исполнителей —
              она разделится на отдельные заказы.
            </p>

            {/* b0bef46e: unified pickup-point contract — one map for every
                non-courier method. «ПВЗ» picks a carrier point on the map
                (required); «Самовывоз у исполнителя» (only when a quote-print
                Pickup line awaits its performer point) keeps the provider
                point chooser below; courier stays the plain address form.
                Print jobs keep their per-line choice; the chips set the
                method for product-only orders. */}
            <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
              <DeliveryChip
                active={deliveryMethod === 'DropoffPoint'}
                label="ПВЗ — пункт выдачи"
                price="карта"
                onClick={() => setDeliveryMethod('DropoffPoint')}
              />
              {pickupNeed && (
                <DeliveryChip
                  active={deliveryMethod === 'Pickup'}
                  label="Самовывоз у исполнителя"
                  price="0 ₽"
                  onClick={() => setDeliveryMethod('Pickup')}
                />
              )}
              {courierAvailable && (
                <DeliveryChip
                  active={deliveryMethod === 'Courier'}
                  label="Курьер по адресу"
                  price={`от ${formatRub(SAME_CITY_DELIVERY_RUB)}`}
                  onClick={() => setDeliveryMethod('Courier')}
                />
              )}
              {singlePerformerId && providerCity && !courierAvailable && (
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0, width: '100%' }}>
                  Курьер недоступен: город доставки не совпадает с городом исполнителя — выберите ПВЗ или самовывоз.
                </p>
              )}
            </div>

            {/* A1 (audit-3): mirrors the server guard in CheckoutAsync — a
                Pickup order without its performer point needs a typed address
                (b0bef46e: a chosen carrier ПВЗ also satisfies the server; the
                ambiguous product-only Pickup chip is no longer offered). */}
            {deliveryMethod === 'Pickup'
              && !submitAddress(chooserMode, selectedAddressId, book, address)
              && !carrierPoint
              && (
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  Самовывоз без пункта и адреса невозможен — укажите адрес или выберите курьера.
                </p>
              )}

            {/* b0bef46e: the ONE map — shown for every non-courier method
                (ПВЗ + самовывоз), never for courier-by-address. The carrier
                point picked here is the single receiving point; the Ozon
                tariff estimate attaches to it (no separate Ozon picker). */}
            {showPickupMap(deliveryMethod) && (
              <>
                <CarrierPickupMapPanel
                  selected={carrierPoint}
                  onSelect={setCarrierPoint}
                  pickupPoints={singlePerformerId ? pickupPoints : null}
                  selectedPickupId={pickupPointId}
                  onPickupSelect={handlePickupSelect}
                  onCityChange={setPickupCity}
                />
                {mapBlock && (
                  <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', margin: 0 }}>
                    {mapBlock}
                  </p>
                )}
              </>
            )}

            {/* 3830fe0d: pickup-point chooser for quote-print lines added with
                Самовывоз (the method rides the line; the point is picked here
                per Artem's upload → cart → delivery point → performer flow). */}
            {pickupNeed && (
              <div>
                <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-xs)' }}>
                  ПУНКТ САМОВЫВОЗА
                </p>
                {loadingPoints ? (
                  <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                    Загружаем пункты самовывоза исполнителя...
                  </p>
                ) : (pickupPoints ?? []).length === 0 ? (
                  <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                    У «{pickupNeed.performerName ?? 'исполнителя'}» нет активных пунктов самовывоза — удалите строку и добавьте её снова с доставкой курьером.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
                    {(pickupPoints ?? []).map((p) => (
                      <PickupPointOption
                        key={p.pickupPointId}
                        active={pickupPointId === p.pickupPointId}
                        address={p.address}
                        workingHours={p.workingHours}
                        onClick={() => setPickupPointId(p.pickupPointId)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CB-3: the address field is a chooser over the address book
                (default entry preselected) with a free-form fallback. */}
            {book.length > 0 && (
              <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
                {book.map((entry) => {
                  const selected = chooserMode === 'book' && selectedAddressId === entry.id;
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setChooserMode('book');
                        setSelectedAddressId(entry.id);
                        setSaveToBook(false);
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        textAlign: 'left',
                        padding: 'var(--space-sm) var(--space-md)',
                        borderRadius: 'var(--radius-md)',
                        border: selected
                          ? '2px solid var(--color-primary)'
                          : '1px solid var(--color-separator)',
                        background: 'var(--color-surface)',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--fs-body)',
                          fontWeight: selected ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                          color: selected ? 'var(--color-primary)' : 'var(--color-text-primary)',
                        }}
                      >
                        {entryTitle(entry)}{entry.isDefault && ' · по умолчанию'}
                      </span>
                      <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
                        {entrySubtitle(entry)}
                      </span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  aria-pressed={chooserMode === 'custom'}
                  onClick={() => {
                    setChooserMode('custom');
                    setSaveToBook(false);
                  }}
                  style={{
                    textAlign: 'left',
                    padding: 'var(--space-sm) var(--space-md)',
                    borderRadius: 'var(--radius-md)',
                    border:
                      chooserMode === 'custom' && address.trim()
                        ? '2px solid var(--color-primary)'
                        : '1px solid var(--color-separator)',
                    background: 'var(--color-surface)',
                    cursor: 'pointer',
                    fontSize: 'var(--fs-body)',
                    color:
                      chooserMode === 'custom' && address.trim()
                        ? 'var(--color-primary)'
                        : 'var(--color-text-secondary)',
                  }}
                >
                  Другой адрес — ввести
                </button>
              </div>
            )}

            {chooserMode === 'custom' && (
              <AddressInput
                value={address}
                onChange={setAddress}
                placeholder="Адрес доставки (необязательно)"
              />
            )}

            {canOfferSaveToBook(chooserMode, address, book) && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  fontSize: 'var(--fs-caption)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <input
                  type="checkbox"
                  checked={saveToBook}
                  onChange={(e) => setSaveToBook(e.target.checked)}
                />
                Сохранить адрес для будущих заказов
              </label>
            )}

            {book.length === 0 && (
              <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                Адреса для быстрого выбора можно сохранить в профиле (иконка профиля справа сверху).
              </p>
            )}
            {/* 527b0a7f (Artem 2026-08-30): recipient phone at checkout —
                prefilled from the profile, snapshotted onto every order (the
                server saves a first-typed number back to the profile). Required
                for delivery orders: the courier/ПВЗ needs a reachable recipient
                and Ozon order/create will validate it when that leg lands. */}
            <div>
              <input
                type="tel"
                inputMode="tel"
                placeholder={
                  phoneRequired
                    ? 'Телефон получателя (обязателен для доставки)'
                    : 'Телефон получателя (необязательно)'
                }
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{
                  height: 44,
                  width: '100%',
                  padding: '0 var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${phoneGate.error || phoneGate.missing ? 'var(--color-danger)' : 'var(--color-separator)'}`,
                  background: 'var(--color-surface)',
                  fontSize: 'var(--fs-body)',
                }}
              />
              {(phoneGate.error || phoneGate.missing) && (
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', margin: 'var(--space-xs) 0 0' }}>
                  {phoneGate.error ?? 'Укажите телефон получателя — курьеру и пункту выдачи нужен получатель.'}
                </p>
              )}
            </div>

            <input
              type="text"
              placeholder="Комментарий исполнителю (необязательно)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{
                height: 44,
                padding: '0 var(--space-md)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-separator)',
                background: 'var(--color-surface)',
                fontSize: 'var(--fs-body)',
              }}
            />

            {/* MS-2 (TASK-004f86db) + MS-3 (TASK-bfa93913): print-color chips —
                shown only while the cart has quote-print lines; the pick rides
                CheckoutDto.PrintColor onto every PerformerCart order. MS-3: the
                palette prefers the performers' in-stock colors (intersection
                across lines — one color rides the whole checkout); sold-out /
                no-common-color keeps the «уточните» posture without chips. */}
            {(colorChoice.colors.length > 0 || colorChoice.mode === 'ask' || offerModeChips) && (
              <div>
                <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-xs)' }}>
                  ЦВЕТ ПЕЧАТИ
                </p>
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: '0 0 var(--space-xs)' }}>
                  {cartColorCaption(colorChoice, cart?.items)}
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                  {colorChoice.colors.map((color) => {
                    const active = colorMode === 'concrete' && printColor === color;
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => { setColorMode('concrete'); setPrintColor(color); }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 'var(--radius-pill)',
                          fontSize: 'var(--fs-caption)',
                          fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-separator)'}`,
                          background: active ? 'rgba(255,149,0,0.08)' : 'var(--color-surface)',
                          color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                        }}
                      >
                        {/* ecfe2257: performer hex as a circular swatch dot;
                            ac930ee2: stock rows without a hand-typed hex get
                            theirs from the backend name→hex map, and the
                            standard-palette posture falls back client-side. */}
                        <span style={swatchDotStyle(hexByColor.get(color.toLowerCase()) ?? fallbackHexFor(color))} />
                        {color}
                      </button>
                    );
                  })}
                  {/* ecfe2257 (Artem 2026-08-28 §5): mode chips — multicolor defers
                      slot mapping to comments/chat; any color bypasses per-color
                      stock limits (total availability only). Offered for every
                      print-job cart, including the sold-out «уточните» posture. */}
                  {offerModeChips && (
                    <>
                      <ModeChip
                        active={colorMode === 'multicolor'}
                        label={MULTICOLOR_CHIP}
                        onClick={() => setColorMode('multicolor')}
                      />
                      <ModeChip
                        active={colorMode === 'any'}
                        label={ANY_COLOR_CHIP}
                        onClick={() => setColorMode('any')}
                      />
                    </>
                  )}
                </div>
                {offerModeChips && colorModeNote(colorMode) && (
                  <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 'var(--space-xs) 0 0' }}>
                    {colorModeNote(colorMode)}
                  </p>
                )}
              </div>
            )}

            <button className="btn btn-primary" disabled={busy || hasBlocking} onClick={() => void checkout()}>
              {busy ? 'Оформляем…' : 'Оформить заказ'}
            </button>
            {hasBlocking && (
              <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
                {pickupBlock(pickupNeed, pickupPointId, pickupPoints)
                  ?? mapBlock
                  ?? 'Исправьте позиции с ошибками (⚠) — затем можно оформить заказ'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* 3830fe0d: one selectable pickup point — full-width row, address + working
   hours, same light-theme selection language as DeliveryChip
   (DESIGN-SYSTEM.md). Moved here from the quote-page PerformerList. */
function PickupPointOption({ active, address, workingHours, onClick }: {
  active: boolean;
  address: string;
  workingHours: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--space-xxs)',
        padding: 'var(--space-xs) var(--space-md)',
        borderRadius: 'var(--radius-md)',
        border: active
          ? '2px solid var(--color-primary)'
          : '1px solid var(--color-separator)',
        background: 'var(--color-surface)',
        cursor: 'pointer',
        textAlign: 'left',
        color: active ? 'var(--color-primary)' : 'var(--color-text-primary)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        fontSize: 'var(--fs-body)',
      }}
    >
      <span>{address}</span>
      {workingHours && (
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', fontWeight: 'var(--fw-regular)' }}>
          {workingHours}
        </span>
      )}
    </button>
  );
}

/* ecfe2257 (Artem 2026-08-28 §5): «Многоцветная»/«Любой цвет» chip — same
   pill anatomy as the concrete color chips (no swatch dot: no single color). */
function ModeChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--fs-caption)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        cursor: 'pointer',
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-separator)'}`,
        background: active ? 'rgba(255,149,0,0.08)' : 'var(--color-surface)',
        color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}
